import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { officeSendTarget } from '../../utils/officeGroups'
import { officeMilesText } from '../../utils/officeAddress'

/**
 * PracticeSendSheet — s123 (Dr. Rami 2026-10-29, canvas "Orella Doctors & Clinicians Home" board F APPROVED; copy v1
 * lines 60–68 APPROVED). "Send to <practice>": 1 · the office (nearest first; the fax goes to that office), 2 · who
 * should see it — "Any clinician at this office" by default (his ruling), or a specific clinician there. Continue hands
 * the send target to the caller (StartCaseSheet's office path). Also opened from a map pin that stands for several
 * clinicians at one address (one office, the same two steps).
 * A portal to document.body: PageWrapper's transform would misplace a fixed sheet (v242.21.0, FB3).
 */
const NAVY = '#15354F'
const BLUE = '#46A8F9'

export default function PracticeSendSheet({ practice, onClose, onChoose, t }) {
  const offices = (practice && practice.offices) || []
  const [officeKey, setOfficeKey] = useState(offices[0] ? offices[0].key : null)
  const [who, setWho] = useState('any') // 'any' | <npi>
  const [pickClinician, setPickClinician] = useState(false)
  useEffect(() => { setWho('any'); setPickClinician(false) }, [officeKey])
  const office = useMemo(() => offices.find((o) => o.key === officeKey) || offices[0] || null, [offices, officeKey])
  if (!practice || !office) return null
  const name = practice.name || office.practice || (office.rep && office.rep.name) || ''
  const clinicians = office.rows || []
  const others = clinicians.slice(0, 2).map((r) => r.name).filter(Boolean)
  const more = Math.max(0, clinicians.length - others.length)
  const lineOf = (o) => {
    const a = o.address || {}
    return [a.line1, a.city].filter(Boolean).join(' · ') || (o.rep && o.rep.name) || ''
  }
  const row = who === 'any' ? null : clinicians.find((r) => r.npi === who) || null
  const choose = () => { const target = officeSendTarget(office, { row }); if (target) onChoose(target) }
  const radio = (on) => ({ border: on ? `1.5px solid ${BLUE}` : '1px solid #E3ECF3', background: on ? '#F5FBFF' : '#FFFFFF' })
  return createPortal((
    <div className="fixed inset-0 z-[60] flex flex-col justify-end" data-no-top-inset="bottom-anchored sheet (justify-end, max 88vh, pays the bottom inset)" style={{ background: 'rgba(21,53,79,0.45)' }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose() }} data-testid="practice-send-sheet">
      <div className="bg-white rounded-t-[24px] px-[18px] pt-3 overflow-y-auto" style={{ maxHeight: '88vh', paddingBottom: 'calc(1.4rem + env(safe-area-inset-bottom, 0px))', boxShadow: '0 -8px 30px rgba(21,53,79,0.2)' }}>
        <div className="w-10 h-1.5 rounded-full mx-auto mb-3" style={{ background: '#D8E6F0' }} />
        <div className="flex items-start gap-2 mb-3.5">
          <div className="flex-1 min-w-0">
            <h3 className="text-[18px] font-extrabold" style={{ color: NAVY }}>{t('doctors.sendToPractice', { defaultValue: 'Send to {{practice}}', practice: name })}</h3>
            <p className="text-[13px] mt-1" style={{ color: '#475569' }}>{t('doctors.sendToPracticeSub', { defaultValue: 'Your Case Report is faxed to the office you pick.' })}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common.close', 'Close')} className="w-9 h-9 rounded-full grid place-items-center flex-none" style={{ color: '#93A5B4' }}>
            <X size={18} />
          </button>
        </div>

        <p className="text-[12px] font-extrabold tracking-wider uppercase mb-2" style={{ color: '#475569' }}>{t('doctors.stepOffice', { defaultValue: '1 · Office' })}</p>
        <div className="flex flex-col gap-2 mb-4" role="radiogroup">
          {offices.map((o) => (
            <button key={o.key} type="button" role="radio" aria-checked={o.key === office.key} onClick={() => setOfficeKey(o.key)}
                    className="w-full text-left rounded-[14px] p-3 flex items-center gap-2.5" style={radio(o.key === office.key)} data-cta="practice-office">
              <span className="w-[18px] h-[18px] rounded-full flex-none grid place-items-center" style={{ border: `2px solid ${o.key === office.key ? BLUE : '#C8D6E2'}` }}>
                {o.key === office.key && <span className="w-2 h-2 rounded-full" style={{ background: BLUE }} />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-bold truncate" style={{ color: NAVY }}>{lineOf(o)}</span>
                <span className="block text-[12px] mt-0.5" style={{ color: '#475569' }}>
                  {[o.rep && typeof o.rep.miles === 'number' ? officeMilesText(o.rep, t) : null,
                    o.rep && o.rep.faxOnFile ? t('findDoctor.faxOnFile', 'Fax on file') : t('findDoctor.noFax', 'No fax listed')].filter(Boolean).join(' · ')}
                </span>
              </span>
            </button>
          ))}
        </div>

        <p className="text-[12px] font-extrabold tracking-wider uppercase mb-2" style={{ color: '#475569' }}>{t('doctors.stepWho', { defaultValue: '2 · Who should see it?' })}</p>
        <div className="flex flex-col gap-2 mb-4" role="radiogroup">
          <button type="button" role="radio" aria-checked={who === 'any'} onClick={() => { setWho('any'); setPickClinician(false) }}
                  className="w-full text-left rounded-[14px] p-3 flex items-center gap-2.5" style={radio(who === 'any')} data-cta="practice-any-clinician">
            <span className="w-[18px] h-[18px] rounded-full flex-none grid place-items-center" style={{ border: `2px solid ${who === 'any' ? BLUE : '#C8D6E2'}` }}>
              {who === 'any' && <span className="w-2 h-2 rounded-full" style={{ background: BLUE }} />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[14px] font-bold" style={{ color: NAVY }}>{t('doctors.anyClinicianHere', { defaultValue: 'Any clinician at this office' })}</span>
              <span className="block text-[12px] mt-0.5" style={{ color: '#475569' }}>{t('doctors.anyClinicianHereSub', { defaultValue: 'The office decides who sees you first' })}</span>
            </span>
          </button>
          <button type="button" role="radio" aria-checked={who !== 'any'} onClick={() => setPickClinician((v) => !v)}
                  className="w-full text-left rounded-[14px] p-3 flex items-center gap-2.5" style={radio(who !== 'any')} data-cta="practice-specific-clinician">
            <span className="w-[18px] h-[18px] rounded-full flex-none grid place-items-center" style={{ border: `2px solid ${who !== 'any' ? BLUE : '#C8D6E2'}` }}>
              {who !== 'any' && <span className="w-2 h-2 rounded-full" style={{ background: BLUE }} />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[14px] font-bold" style={{ color: NAVY }}>{t('doctors.specificClinician', { defaultValue: 'A specific clinician' })}</span>
              <span className="block text-[12px] mt-0.5 truncate" style={{ color: '#475569' }}>
                {row ? `${row.name}${row.credential ? `, ${row.credential}` : ''}`
                  : t('doctors.specificClinicianSub', { defaultValue: '{{names}} and {{n}} more at this office', names: others.join(', '), n: more })}
              </span>
            </span>
          </button>
          {pickClinician && (
            <div className="flex flex-col rounded-[14px] overflow-hidden" style={{ border: '1px solid #E3ECF3' }} data-testid="practice-clinician-list">
              {clinicians.map((r) => (
                <button key={r.npi} type="button" onClick={() => { setWho(r.npi); setPickClinician(false) }}
                        className="w-full text-left px-3 py-2.5 flex items-center gap-2" style={{ borderBottom: '1px solid #EDF2F7', background: who === r.npi ? '#F5FBFF' : '#FFFFFF' }}>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13.5px] font-bold truncate" style={{ color: NAVY }}>{r.name}{r.credential ? `, ${r.credential}` : ''}</span>
                    {r.specialty && <span className="block text-[12px] truncate" style={{ color: '#475569' }}>{r.specialty}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button type="button" onClick={choose} data-cta="practice-continue"
                className="w-full rounded-full py-3.5 text-[15px] font-bold text-white" style={{ background: NAVY }}>
          {t('common.continue', 'Continue')}
        </button>
      </div>
    </div>
  ), document.body)
}
